import React, { useState } from "react";
import useSWR, { mutate } from "swr";
import api from "@/lib/api";
import { gbp } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Truck, Plus, Pencil, Trash2, Loader2, Save } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const fetcher = (url) => api.get(url).then((r) => r.data);

const emptyVehicle = {
  registration: "", make: "", model: "", year: "",
  assigned_driver_id: "", depot: "", lease_cost: "", status: "active",
};

const Vehicles = () => {
  const { data, isLoading } = useSWR("/vehicles", fetcher);
  const { data: drivers } = useSWR("/drivers", fetcher);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyVehicle);

  // Edit / Delete state
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const openEdit = (v) => {
    setEditing(v);
    setEditForm({
      registration: v.registration || "",
      make: v.make || "",
      model: v.model || "",
      year: v.year || "",
      assigned_driver_id: v.assigned_driver_id || "",
      depot: v.depot || "",
      lease_cost: v.lease_cost ?? "",
      status: v.status || "active",
    });
  };
  const setE = (k, v) => setEditForm((f) => ({ ...f, [k]: v }));
  const submitEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/vehicles/${editing.id}`, {
        ...editForm,
        year: editForm.year ? parseInt(editForm.year, 10) : null,
        lease_cost: editForm.lease_cost !== "" ? parseFloat(editForm.lease_cost) : 0,
      });
      toast.success("Vehicle updated");
      setEditing(null);
      mutate("/vehicles");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
    setSaving(false);
  };
  const doDelete = async () => {
    if (!deleting) return;
    try {
      await api.delete(`/vehicles/${deleting.id}`);
      toast.success(`Vehicle ${deleting.registration} deleted`);
      setDeleting(null);
      mutate("/vehicles");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/vehicles", {
        ...form,
        year: form.year ? parseInt(form.year, 10) : undefined,
        lease_cost: form.lease_cost ? parseFloat(form.lease_cost) : 0,
      });
      toast.success("Vehicle added");
      setOpen(false);
      setForm(emptyVehicle);
      mutate("/vehicles");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  return (
    <div className="p-4 md:p-8 space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-brand-red font-mono">Fleet</div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-brand-text mt-1">Vehicles</h1>
          <p className="text-sm text-slate-500 mt-1">{data?.length || 0} vehicles registered.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-brand-red hover:bg-brand-red-hover" data-testid="vehicle-add-btn">
              <Plus className="w-4 h-4 mr-1.5" /> Add vehicle
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New vehicle</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div><Label className="text-xs uppercase tracking-wider text-slate-500">Registration</Label>
                <Input required data-testid="veh-reg" value={form.registration}
                       onChange={(e) => setForm((f) => ({ ...f, registration: e.target.value }))} /></div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label className="text-xs uppercase tracking-wider text-slate-500">Make</Label>
                  <Input value={form.make} onChange={(e) => setForm((f) => ({ ...f, make: e.target.value }))} /></div>
                <div><Label className="text-xs uppercase tracking-wider text-slate-500">Model</Label>
                  <Input value={form.model} onChange={(e) => setForm((f) => ({ ...f, model: e.target.value }))} /></div>
                <div><Label className="text-xs uppercase tracking-wider text-slate-500">Year</Label>
                  <Input type="number" value={form.year} onChange={(e) => setForm((f) => ({ ...f, year: e.target.value }))} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs uppercase tracking-wider text-slate-500">Depot</Label>
                  <Input value={form.depot} onChange={(e) => setForm((f) => ({ ...f, depot: e.target.value }))} /></div>
                <div><Label className="text-xs uppercase tracking-wider text-slate-500">Lease £/mo</Label>
                  <Input type="number" step="0.01" value={form.lease_cost}
                         onChange={(e) => setForm((f) => ({ ...f, lease_cost: e.target.value }))} /></div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Assigned driver</Label>
                <Select value={form.assigned_driver_id}
                        onValueChange={(v) => setForm((f) => ({ ...f, assigned_driver_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Unassigned" /></SelectTrigger>
                  <SelectContent>
                    {(drivers || []).map((d) => (
                      <SelectItem key={d.id} value={d.id}>{d.full_name} ({d.driver_code})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button type="submit" data-testid="veh-save" className="bg-brand-red hover:bg-brand-red-hover">Save</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {isLoading && <div className="text-sm text-slate-500">Loading…</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="vehicles-grid">
        {(data || []).map((v) => {
          const driver = (drivers || []).find((d) => d.id === v.assigned_driver_id);
          return (
            <Card key={v.id} className="p-5 rounded-2xl border-brand-border hover:shadow-md hover:-translate-y-0.5 transition-[transform,box-shadow] duration-200" data-testid={`vehicle-${v.registration}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-[var(--brand-navy)] text-white grid place-items-center">
                    <Truck className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-heading font-bold text-brand-text tracking-tight font-mono">{v.registration}</div>
                    <div className="text-[11px] text-slate-500">{v.make} {v.model} · {v.year}</div>
                  </div>
                </div>
                <Badge variant="outline" className="text-[10px] uppercase tracking-wider">{v.status}</Badge>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
                <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Driver</div>
                  <div className="mt-0.5 text-brand-text">{driver?.full_name || "—"}</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Depot</div>
                  <div className="mt-0.5 text-brand-text">{v.depot || "—"}</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Lease /mo</div>
                  <div className="mt-0.5 font-heading font-bold text-brand-text">{gbp(v.lease_cost)}</div></div>
              </div>
              <div className="mt-4 pt-3 border-t border-brand-border flex items-center justify-end gap-1">
                <Button size="sm" variant="ghost" onClick={() => openEdit(v)} data-testid={`veh-edit-${v.registration}`}>
                  <Pencil className="w-3.5 h-3.5" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setDeleting(v)} data-testid={`veh-del-${v.registration}`}>
                  <Trash2 className="w-3.5 h-3.5 text-red-600" />
                </Button>
              </div>
            </Card>
          );
        })}
        {!isLoading && (data || []).length === 0 && (
          <div className="col-span-full text-center py-12 text-slate-500 text-sm">
            <Truck className="w-6 h-6 mx-auto mb-2 opacity-40" />No vehicles.
          </div>
        )}
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent data-testid="vehicle-edit-dialog">
          <DialogHeader><DialogTitle>Edit {editing?.registration}</DialogTitle></DialogHeader>
          <form onSubmit={submitEdit} className="space-y-3">
            <div><Label className="text-xs uppercase tracking-wider text-slate-500">Registration</Label>
              <Input required data-testid="veh-edit-reg" value={editForm.registration}
                     onChange={(e) => setE("registration", e.target.value)} /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs uppercase tracking-wider text-slate-500">Make</Label>
                <Input value={editForm.make} onChange={(e) => setE("make", e.target.value)} /></div>
              <div><Label className="text-xs uppercase tracking-wider text-slate-500">Model</Label>
                <Input value={editForm.model} onChange={(e) => setE("model", e.target.value)} /></div>
              <div><Label className="text-xs uppercase tracking-wider text-slate-500">Year</Label>
                <Input type="number" value={editForm.year} onChange={(e) => setE("year", e.target.value)} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase tracking-wider text-slate-500">Depot</Label>
                <Input value={editForm.depot} onChange={(e) => setE("depot", e.target.value)} /></div>
              <div><Label className="text-xs uppercase tracking-wider text-slate-500">Lease £/mo</Label>
                <Input type="number" step="0.01" value={editForm.lease_cost}
                       onChange={(e) => setE("lease_cost", e.target.value)} /></div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Assigned driver</Label>
              <Select value={editForm.assigned_driver_id || ""}
                      onValueChange={(v) => setE("assigned_driver_id", v)}>
                <SelectTrigger><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent>
                  {(drivers || []).map((d) => (
                    <SelectItem key={d.id} value={d.id}>{d.full_name} ({d.driver_code})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Status</Label>
              <Select value={editForm.status} onValueChange={(v) => setE("status", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="maintenance">In maintenance</SelectItem>
                  <SelectItem value="off_road">Off road</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="submit" disabled={saving} data-testid="veh-edit-save" className="bg-brand-red hover:bg-brand-red-hover">
                {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
                Save changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}>
        <AlertDialogContent data-testid="vehicle-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleting?.registration}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the vehicle from your fleet. Any driver currently assigned will be unassigned.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700"
                                data-testid="veh-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Vehicles;
